// REQ-P1-06b: 共通チャットノートの契約型付きAPI。

import { api, type paths } from './client';
import { uploadFileToPresignedUrl as uploadFileToStoragePresignedUrl } from './storage';
import { ApiStatusError } from './templates';

type NoteListResponse = NonNullable<
  paths['/admin/personal-chat-notes/{id}']['get']['responses'][200]['content']
>['application/json'];
type EventNoteListResponse = NonNullable<
  paths['/admin/event-notes/{id}']['get']['responses'][200]['content']
>['application/json'];
type NoteDetailResponse = NonNullable<
  paths['/personal-chat-notes/detail/{noteId}']['get']['responses'][200]['content']
>['application/json'];
type EventNoteDetailResponse = NonNullable<
  paths['/event-notes/detail/{noteId}']['get']['responses'][200]['content']
>['application/json'];
type NoteImageInput = NonNullable<
  paths['/admin/personal-chat-notes']['post']['requestBody']
>['content']['application/json']['images'] extends Array<infer T> | undefined ? T : never;
type PersonalNoteCreateRequest = NonNullable<
  paths['/admin/personal-chat-notes']['post']['requestBody']
>['content']['application/json'];
type ThreadNoteCreateRequest = NonNullable<
  paths['/admin/group-chat-notes']['post']['requestBody']
>['content']['application/json'];
type EventNoteCreateRequest = NonNullable<
  paths['/admin/event-notes']['post']['requestBody']
>['content']['application/json'];
type NoteUpdateRequest = NonNullable<
  paths['/admin/personal-chat-notes/{id}']['put']['requestBody']
>['content']['application/json'];
type NoteCreateResponse = NonNullable<
  paths['/admin/personal-chat-notes']['post']['responses'][200]['content']
>['application/json'];
type SuccessResponse = NonNullable<
  paths['/admin/personal-chat-notes/{id}']['put']['responses'][200]['content']
>['application/json'];
type StoragePresignPutRequest = NonNullable<
  paths['/storage/presign-put']['post']['requestBody']
>['content']['application/json'];
type StoragePresignPutResponse = NonNullable<
  paths['/storage/presign-put']['post']['responses'][200]['content']
>['application/json'];
type NoteImageUploadRequest = NonNullable<
  paths['/storage/note-images/upload']['post']['requestBody']
>['content']['multipart/form-data'];
type NoteImageUploadResponse = NonNullable<
  paths['/storage/note-images/upload']['post']['responses'][200]['content']
>['application/json'];

type NoteActor = 'admin' | 'artist';
type NoteKind = 'personal' | 'group' | 'event';
type ParsedNoteResource = {
  actor: NoteActor;
  kind: NoteKind;
  id: string;
  query: URLSearchParams;
};
type ParsedNoteDetailResource = {
  kind: NoteKind;
  noteId: string;
  query: URLSearchParams;
};
export type ChatNoteImageInput = NoteImageInput;
export type ChatNoteListResponse = NoteListResponse | EventNoteListResponse;
export type ChatNoteDetailResponse = NoteDetailResponse | EventNoteDetailResponse;
export type ChatNoteCreateBody = {
  threadId?: string;
  eventId?: string;
  title: string;
  content: string;
  images?: NoteImageInput[];
  saveMode?: 'draft' | 'publish' | 'direct';
};

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed.message === 'string') return parsed.message;
      if (parsed && typeof parsed.error === 'string') return parsed.error;
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiStatusError(status: number, error?: unknown, fallbackMessage?: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage || statusError.message;
  throw statusError;
}

function ensureData<T>(data: T | undefined, response: Response, error?: unknown, fallbackMessage?: string): T {
  if (!response.ok || !data) throwApiStatusError(response.status, error, fallbackMessage);
  return data;
}

async function ensureOk(response: Response, error?: unknown, fallbackMessage?: string): Promise<void> {
  if (response.ok) return;
  throwApiStatusError(response.status, error, fallbackMessage);
}

function resolveUrl(value: string): URL {
  const base =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost';
  return new URL(value, base);
}

function toApiPath(value: string): { pathname: string; query: URLSearchParams } {
  const url = resolveUrl(value);
  const pathname = url.pathname.replace(/^\/api(?=\/)/, '') || '/';
  return { pathname, query: url.searchParams };
}

function parseNoteCollectionUrl(value: string): ParsedNoteResource {
  const { pathname, query } = toApiPath(value);
  const parts = pathname.split('/').filter(Boolean);
  const [actor, resource, id] = parts;
  if ((actor !== 'admin' && actor !== 'artist') || !id || parts.length !== 3) {
    throw new Error(`Unsupported note collection path: ${pathname}`);
  }
  if (resource === 'personal-chat-notes') return { actor, kind: 'personal', id, query };
  if (resource === 'group-chat-notes') return { actor, kind: 'group', id, query };
  if (resource === 'event-notes') return { actor, kind: 'event', id, query };
  throw new Error(`Unsupported note collection path: ${pathname}`);
}

function parseNoteMutationUrl(value: string): ParsedNoteResource {
  const { pathname, query } = toApiPath(value);
  const parts = pathname.split('/').filter(Boolean);
  const [actor, resource, id] = parts;
  if ((actor !== 'admin' && actor !== 'artist') || parts.length < 2 || parts.length > 3) {
    throw new Error(`Unsupported note mutation path: ${pathname}`);
  }
  if (resource === 'personal-chat-notes') return { actor, kind: 'personal', id: id || '', query };
  if (resource === 'group-chat-notes') return { actor, kind: 'group', id: id || '', query };
  if (resource === 'event-notes') return { actor, kind: 'event', id: id || '', query };
  throw new Error(`Unsupported note mutation path: ${pathname}`);
}

function parseNoteDetailUrl(value: string): ParsedNoteDetailResource {
  const { pathname, query } = toApiPath(value);
  const parts = pathname.split('/').filter(Boolean);
  const [resource, detail, noteId] = parts;
  if (detail !== 'detail' || !noteId || parts.length !== 3) {
    throw new Error(`Unsupported note detail path: ${pathname}`);
  }
  if (resource === 'personal-chat-notes') return { kind: 'personal', noteId, query };
  if (resource === 'group-chat-notes') return { kind: 'group', noteId, query };
  if (resource === 'event-notes') return { kind: 'event', noteId, query };
  throw new Error(`Unsupported note detail path: ${pathname}`);
}

function readPaging(query: URLSearchParams): { offset: number; limit: number } {
  const offset = Number(query.get('offset') || 0);
  const limit = Number(query.get('limit') || 20);
  return {
    offset: Number.isFinite(offset) ? offset : 0,
    limit: Number.isFinite(limit) ? limit : 20,
  };
}

export async function listChatNotesByUrl(url: string): Promise<ChatNoteListResponse> {
  const parsed = parseNoteCollectionUrl(url);
  const query = readPaging(parsed.query);
  if (parsed.kind === 'personal' && parsed.actor === 'admin') {
    const { data, response } = await api.GET('/admin/personal-chat-notes/{id}', {
      params: { path: { id: parsed.id }, query },
    });
    return ensureData(data as NoteListResponse | undefined, response);
  }
  if (parsed.kind === 'personal') {
    const { data, response } = await api.GET('/artist/personal-chat-notes/{id}', {
      params: { path: { id: parsed.id }, query },
    });
    return ensureData(data as NoteListResponse | undefined, response);
  }
  if (parsed.kind === 'group' && parsed.actor === 'admin') {
    const { data, response } = await api.GET('/admin/group-chat-notes/{id}', {
      params: { path: { id: parsed.id }, query },
    });
    return ensureData(data as NoteListResponse | undefined, response);
  }
  if (parsed.kind === 'group') {
    const { data, response } = await api.GET('/artist/group-chat-notes/{id}', {
      params: { path: { id: parsed.id }, query },
    });
    return ensureData(data as NoteListResponse | undefined, response);
  }
  if (parsed.actor === 'admin') {
    const { data, response } = await api.GET('/admin/event-notes/{id}', {
      params: { path: { id: parsed.id }, query },
    });
    return ensureData(data as EventNoteListResponse | undefined, response);
  }
  const { data, response } = await api.GET('/artist/event-notes/{id}', {
    params: { path: { id: parsed.id }, query },
  });
  return ensureData(data as EventNoteListResponse | undefined, response);
}

export async function getChatNoteDetailByUrl(url: string): Promise<ChatNoteDetailResponse> {
  const parsed = parseNoteDetailUrl(url);
  const version = parsed.query.get('version') || undefined;
  const params = version
    ? ({ path: { noteId: parsed.noteId }, query: { version } } as any)
    : ({ path: { noteId: parsed.noteId } } as any);
  if (parsed.kind === 'personal') {
    const { data, response } = await api.GET('/personal-chat-notes/detail/{noteId}', {
      params,
    });
    return ensureData(data as NoteDetailResponse | undefined, response);
  }
  if (parsed.kind === 'group') {
    const { data, response } = await api.GET('/group-chat-notes/detail/{noteId}', {
      params,
    });
    return ensureData(data as NoteDetailResponse | undefined, response);
  }
  const { data, response } = await api.GET('/event-notes/detail/{noteId}', {
    params,
  });
  return ensureData(data as EventNoteDetailResponse | undefined, response);
}

export async function createChatNoteByUrl(
  url: string,
  body: ChatNoteCreateBody
): Promise<NoteCreateResponse> {
  const parsed = parseNoteMutationUrl(url);
  if (parsed.kind === 'personal' && parsed.actor === 'admin') {
    const { data, error, response } = await api.POST('/admin/personal-chat-notes', {
      body: body as PersonalNoteCreateRequest,
    });
    return ensureData(data as NoteCreateResponse | undefined, response, error, 'ノートの作成に失敗しました。');
  }
  if (parsed.kind === 'personal') {
    const { data, error, response } = await api.POST('/artist/personal-chat-notes', {
      body: body as PersonalNoteCreateRequest,
    });
    return ensureData(data as NoteCreateResponse | undefined, response, error, 'ノートの作成に失敗しました。');
  }
  if (parsed.kind === 'group' && parsed.actor === 'admin') {
    const { data, error, response } = await api.POST('/admin/group-chat-notes', {
      body: body as ThreadNoteCreateRequest,
    });
    return ensureData(data as NoteCreateResponse | undefined, response, error, 'ノートの作成に失敗しました。');
  }
  if (parsed.kind === 'group') {
    const { data, error, response } = await api.POST('/artist/group-chat-notes', {
      body: body as ThreadNoteCreateRequest,
    });
    return ensureData(data as NoteCreateResponse | undefined, response, error, 'ノートの作成に失敗しました。');
  }
  if (parsed.actor === 'admin') {
    const { data, error, response } = await api.POST('/admin/event-notes', {
      body: body as EventNoteCreateRequest,
    });
    return ensureData(data as NoteCreateResponse | undefined, response, error, 'ノートの作成に失敗しました。');
  }
  const { data, error, response } = await api.POST('/artist/event-notes', {
    body: body as EventNoteCreateRequest,
  });
  return ensureData(data as NoteCreateResponse | undefined, response, error, 'ノートの作成に失敗しました。');
}

export async function updateChatNoteByUrl(
  url: string,
  body: NoteUpdateRequest & { saveMode?: 'draft' | 'publish' | 'direct' }
): Promise<SuccessResponse> {
  const parsed = parseNoteMutationUrl(url);
  if (!parsed.id) throw new Error('Note id is required for update.');
  if (parsed.kind === 'personal' && parsed.actor === 'admin') {
    const { data, error, response } = await api.PUT('/admin/personal-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
      body,
    });
    return ensureData(data as SuccessResponse | undefined, response, error, 'ノートの更新に失敗しました。');
  }
  if (parsed.kind === 'personal') {
    const { data, error, response } = await api.PUT('/artist/personal-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
      body,
    });
    return ensureData(data as SuccessResponse | undefined, response, error, 'ノートの更新に失敗しました。');
  }
  if (parsed.kind === 'group' && parsed.actor === 'admin') {
    const { data, error, response } = await api.PUT('/admin/group-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
      body,
    });
    return ensureData(data as SuccessResponse | undefined, response, error, 'ノートの更新に失敗しました。');
  }
  if (parsed.kind === 'group') {
    const { data, error, response } = await api.PUT('/artist/group-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
      body,
    });
    return ensureData(data as SuccessResponse | undefined, response, error, 'ノートの更新に失敗しました。');
  }
  if (parsed.actor === 'admin') {
    const { data, error, response } = await api.PUT('/admin/event-notes/{id}', {
      params: { path: { id: parsed.id } },
      body,
    });
    return ensureData(data as SuccessResponse | undefined, response, error, 'ノートの更新に失敗しました。');
  }
  const { data, error, response } = await api.PUT('/artist/event-notes/{id}', {
    params: { path: { id: parsed.id } },
    body,
  });
  return ensureData(data as SuccessResponse | undefined, response, error, 'ノートの更新に失敗しました。');
}

export async function deleteChatNoteByUrl(url: string): Promise<void> {
  const parsed = parseNoteMutationUrl(url);
  if (!parsed.id) throw new Error('Note id is required for delete.');
  if (parsed.kind === 'personal' && parsed.actor === 'admin') {
    const { error, response } = await api.DELETE('/admin/personal-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
    });
    await ensureOk(response, error, 'ノートの削除に失敗しました。');
    return;
  }
  if (parsed.kind === 'personal') {
    const { error, response } = await api.DELETE('/artist/personal-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
    });
    await ensureOk(response, error, 'ノートの削除に失敗しました。');
    return;
  }
  if (parsed.kind === 'group' && parsed.actor === 'admin') {
    const { error, response } = await api.DELETE('/admin/group-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
    });
    await ensureOk(response, error, 'ノートの削除に失敗しました。');
    return;
  }
  if (parsed.kind === 'group') {
    const { error, response } = await api.DELETE('/artist/group-chat-notes/{id}', {
      params: { path: { id: parsed.id } },
    });
    await ensureOk(response, error, 'ノートの削除に失敗しました。');
    return;
  }
  if (parsed.actor === 'admin') {
    const { error, response } = await api.DELETE('/admin/event-notes/{id}', {
      params: { path: { id: parsed.id } },
    });
    await ensureOk(response, error, 'ノートの削除に失敗しました。');
    return;
  }
  const { error, response } = await api.DELETE('/artist/event-notes/{id}', {
    params: { path: { id: parsed.id } },
  });
  await ensureOk(response, error, 'ノートの削除に失敗しました。');
}

export async function createNoteImagePresignPut(
  body: StoragePresignPutRequest
): Promise<StoragePresignPutResponse> {
  const { data, response } = await api.POST('/storage/presign-put', { body });
  return ensureData(data as StoragePresignPutResponse | undefined, response);
}

export async function uploadNoteImageThroughApi(file: File): Promise<NoteImageUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const { data, error, response } = await api.POST('/storage/note-images/upload', {
    body: formData as unknown as NoteImageUploadRequest,
  });
  return ensureData(data as NoteImageUploadResponse | undefined, response, error, '画像のアップロードに失敗しました。');
}

export async function uploadFileToPresignedUrl(url: string, file: File): Promise<void> {
  return uploadFileToStoragePresignedUrl(url, file, {
    'Content-Type': file.type || 'application/octet-stream',
  });
}
